import React from 'react'
import qs from 'query-string'
import { useFormikContext } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import {
    setTemplateFilter,
    getCampaignTemplates,
    selectTemplatesFilter,
    OBJECTIVE,
    CampaignTemplateStep,
    updateTemplateData,
} from 'state/campaignTemplates'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import RectangleButton from 'particles/Button/RectangleButton'
import { TemplateFilterSchema } from './index'
import { TemplateContentContainer } from '../style'
import BrandDropdown from 'components/Campaign/BrandDropdown'
import { useSelectedBrand } from 'components/Campaign/BrandDropdown/subscriber'
import { fieldIdentifiers } from 'components/Shared/Comment'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import TemplateInputFieldContainer from 'components/Shared/Permission/TemplateInputFieldContainer'
import OnFormikValueChange, { OnFormikValueChangeProps } from '../OnFormikValueChange'
import { selectCampaignByTemplateData } from 'state'
import { useCheckEditTemplatedCampaignPermission, usePermission } from 'hooks/permissions'
import { CampaignStatusControl, TemplatedCampaignManagement } from 'components/Shared/Permission/enums'
import SaveChanges from '../SaveChanges'
import { resetTemplateFormError } from 'state/error/campaignTemplates'

interface BrandSelectionProps {
    completeToNextSection: () => void
    validateSpecificField: OnFormikValueChangeProps['validateSpecificField']
}

const BrandSelection: React.FC<BrandSelectionProps> = ({ completeToNextSection, validateSpecificField }) => {
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const { setTouched } = useFormikContext<TemplateFilterSchema>()
    // editingCampaignCountry is used to provide faster country retrieval for number input budgetCurrencySign
    // because, waiting brandList is took some time
    const templateFilter = useSelector(selectTemplatesFilter)
    const campaign = useSelector(selectCampaignByTemplateData)
    const canLaunchCampaign = usePermission(CampaignStatusControl.LaunchCampaign)
    const canEdit = useCheckEditTemplatedCampaignPermission(campaign?.status)
    const canEditLiveCampaignBrand = usePermission(TemplatedCampaignManagement.LiveCampaignEditBriefsAndDeadlines)
    const [selectedBrand] = useSelectedBrand()
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    const enableAutoSave = (() => {
        // disable auto-save when live and template not selected
        if (isCampaignLive || !templateDataUuid) {
            return false
        }

        // enable auto-save `for review` only for internal reviewer
        if (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW) {
            return canLaunchCampaign
        }

        return canEdit
    })()

    const onNextClick = () => {
        setTouched({
            brandUuid: true,
        })
        validateSpecificField(['brandUuid']).then(errors => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                dispatch(
                    setTemplateFilter({
                        country: selectedBrand?.country,
                        brandUuid: selectedBrand?.uuid,
                    }),
                )
                if (templateDataUuid)
                    dispatch(
                        updateTemplateData({
                            templateDataUuid: templateDataUuid as string,
                            brandUuid: selectedBrand?.uuid,
                        }),
                    )
                dispatch(getCampaignTemplates({ filter: { country: selectedBrand?.country, limit: 100 }, type: 'all' }))
                if (
                    templateFilter.objective === OBJECTIVE.appPromotion ||
                    templateFilter.objective === OBJECTIVE.content
                ) {
                    dispatch(
                        getCampaignTemplates({
                            filter: {
                                country: selectedBrand?.country,
                                objective: templateFilter.objective,
                            },
                            type: 'filter',
                        }),
                    )
                }
                completeToNextSection()
            }
        })
    }

    const onSaveLiveCampaign = (onSuccessCallback: VoidFunction) => {
        const setTouchedFields: Record<string, boolean> = {}
        const fieldsToUpdate: Record<string, string | number | undefined | null> = {}
        if (canEditLiveCampaignBrand) {
            setTouchedFields['brandUuid'] = true
            fieldsToUpdate['brandUuid'] = selectedBrand?.uuid
        }
        validateSpecificField(Object.keys(fieldsToUpdate) as ['brandUuid']).then(errors => {
            setTouched(setTouchedFields)
            const noError = Object.keys(errors).length === 0
            if (noError) {
                if (templateDataUuid) {
                    dispatch(
                        updateTemplateData({
                            templateDataUuid: templateDataUuid as string,
                            ...fieldsToUpdate,
                            showSuccessToast: true,
                        }),
                    )
                    onSuccessCallback()
                }
            }
        })
    }

    const onBrandUpdateSuccess = () => {
        // Remove template Error
        dispatch(resetTemplateFormError())
    }

    return (
        <>
            <BrandSelectionContainer>
                <OnFormikValueChange
                    fields={['brandUuid']}
                    callback={values => {
                        if (templateDataUuid) {
                            dispatch(
                                updateTemplateData({
                                    templateDataUuid: templateDataUuid as string,
                                    brandUuid: values?.brandUuid,
                                }),
                            )
                        }
                    }}
                    skipCallbackError={{
                        brandUuid: t('errorMessage.validation.selectBrandSameCountry'),
                    }}
                    validateSpecificField={validateSpecificField}
                    disableAutoSave={!enableAutoSave}
                />
                <div className="form-title">
                    <Text scale="H4" color="primary">
                        {t('campaign.template.step1.brandSelection.title')}
                    </Text>
                </div>
                <div className="brand-dropdown">
                    <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                        <BrandDropdown
                            identifier={`${CampaignTemplateStep.BRAND_AND_BUDGET}_${fieldIdentifiers.templateBrand}`}
                            isReadOnly={campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH || undefined}
                            onBrandUpdateSuccess={onBrandUpdateSuccess}
                        />
                    </TemplateInputFieldContainer>
                </div>

                <EditTemplateRestricted>
                    <div className="button">
                        <RectangleButton type={'button'} size={'l'} label={t('button.next')} onClick={onNextClick} />
                    </div>
                </EditTemplateRestricted>
            </BrandSelectionContainer>
            <SaveChanges onSave={onSaveLiveCampaign} checkForLiveEditBriefAndDeadline />
        </>
    )
}

export const BrandSelectionContainer = styled(TemplateContentContainer)`
    max-width: 770px;
    .brand-dropdown {
        width: 770px;
    }
`
export default BrandSelection
